import { logger } from '../../lib/logger';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import {
  ArrowRight, MapPin, FileText, Lock, CheckCircle2, ExternalLink, ChevronLeft,
  Sparkles, X, Home, Users, Calendar, ShieldCheck, Leaf, Award, Hourglass, Star,
} from 'lucide-react';
import { BRAND, RESORT_VIDEOS, DEFAULT_RESORT_HERO_VIDEO, RESORT_SECONDARY_VIDEOS, LOMBOK_HERO_VIDEOS } from '../../lib/brand';
import { RESORT_FINANCIALS, MANIFESTO, LAST20_BENEFITS, fmtEUR } from '../../lib/financial';
import { BROCHURE_DATA } from '../../lib/brochure';
import { RESORT_FAQS, pickText } from '../../lib/resortFaqs';
import RoiCalculator from '../../components/RoiCalculator';
import ApartmentShowcase from '../../components/ApartmentShowcase';
import PropertyUnitsTable from '../../components/PropertyUnitsTable';
import LombokUnitsTable from '../../components/LombokUnitsTable';
import LombokEquitySocio from '../../components/LombokEquitySocio';
import LivePaymentPlan from '../../components/LivePaymentPlan';
import PaymentSimulator from '../../components/PaymentSimulator';
import ProjectDocuments from '../../components/ProjectDocuments';
import LegalDisclaimerBox from '../../components/LegalDisclaimerBox';
import BookSeniorModal from '../../components/BookSeniorModal';
import VisitsCounter, { useTrackVisit } from '../../components/VisitsCounter';
import FakeViewersBadge from '../../components/FakeViewersBadge';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from '../../components/ui/carousel';
import { toast } from 'sonner';

import { useQualification } from '../../contexts/QualificationContext';
import { attachScrollDepthTracker, trackViewContent } from '../../lib/pixel';

export default function ResortDetailPage() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const nav = useNavigate();
  const { open: openQualifica } = useQualification();

  // ★ 2026-08-11 (Fase 37) — Traccia la visita per il contatore "ultimi 7 giorni"
  useTrackVisit(slug);

  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const financial = RESORT_FINANCIALS[slug];

  const [gateOpen, setGateOpen] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // ★ 2026-08-14 (Fase 51) — Modal Prenota Zoom con Senior/CEO (usato ovunque
  // ci sia un CTA "info" o "prenota call" per garantire flow unificato).
  const [bookOpen, setBookOpen] = useState(false);
  const [bookContext, setBookContext] = useState({});
  const openBookSenior = (source) => {
    setBookContext({
      subject: `Info ${slug || 'progetto'}`,
      sourceLabel: source || 'resort_page',
    });
    setBookOpen(true);
  };
  const [form, setForm] = useState({
    name: '', email: '', phone: '', interest_type: 'both', consent_privacy: true,
  });

  // Default mode: equity (always available); can toggle to real_estate if available.
  const reAvailable = !!financial?.realEstate?.available;
  const [mode, setMode] = useState('equity');

  useEffect(() => {
    setLoading(true);
    api.get(`/public/properties/${slug}`).then((r) => setP(r.data)).catch(() => {
      toast.error(t('resortDetail.gate.notFound', 'Resort non trovato'));
      nav('/');
    }).finally(() => setLoading(false));
  }, [slug, nav, t]);

  // ── Meta Pixel: ViewContent + scroll depth on resort detail page ─────
  useEffect(() => {
    if (!slug) return;
    trackViewContent({
      name: `resort_${slug}`,
      category: 'resort_detail',
      contentId: slug,
    });
    const teardown = attachScrollDepthTracker(`resort_${slug}`);
    return teardown;
  }, [slug]);

  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">{t('common.loading', 'Caricamento…')}</div>;
  if (!p || !financial) return null;

  const comingSoon = financial.comingSoon;
  const gallery = p.gallery || [];
  const galleryGroups = p.gallery_groups || [];
  const googleMaps = p.google_maps || null;

  const openGate = (interest) => {
    setForm((f) => ({ ...f, interest_type: interest }));
    setGateOpen(true);
  };

  const submit = async () => {
    if (!form.name || !form.email) { toast.error(t('resortDetail.gate.errorNameEmail', 'Nome ed email obbligatori')); return; }
    if (!form.consent_privacy) { toast.error(t('resortDetail.gate.errorConsent', 'Devi accettare la privacy')); return; }
    setSubmitting(true);
    try {
      const r = await api.post('/public/property-lead', { property_slug: slug, ...form });
      setSubmitted(r.data);
      toast.success(t('resortDetail.gate.successToast', 'Documenti sbloccati! Te li abbiamo anche inviati per email.'));
    } catch (err) {
      logger.debug('ResortDetailPage materials submit failed', err);
      toast.error(t('resortDetail.gate.errorSubmit', 'Errore. Riprova.'));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--surface-1)]" data-testid={`resort-detail-${slug}`}>
      {/* Hero */}
      <section className="relative h-[78vh] min-h-[480px] md:min-h-[520px] overflow-hidden">
        <img src={p.hero_image || p.image_url} alt={p.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/30 to-[var(--surface-1)]" />
        <header className="relative z-10 px-4 md:px-12 py-4 md:py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-white">
            <img src={BRAND.greenParadiseLogoSmall} alt="Green Paradise" className="h-9 md:h-9 w-auto bg-white/10 rounded-md p-1" />
            <div className="hidden sm:block leading-tight">
              <div className="font-serif text-base">Green Paradise</div>
              <div className="text-[9px] tracking-[0.25em] uppercase opacity-80">HCF Property</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-white text-xs hover:opacity-80 inline-flex items-center gap-1"><ChevronLeft className="w-3 h-3" /> {t('resortDetail.allResorts', 'Tutti i resort')}</Link>
            {/* ★ 2026-08-14 (Fase 55) — Selettore lingua sul header resort */}
            <LanguageSwitcher variant="compact" className="bg-white/10 border-white/25 text-white hover:bg-white/20" />
          </div>
        </header>

        <div className="relative z-10 px-4 md:px-12 mt-6 md:mt-16 max-w-4xl text-white">
          <div className="flex items-center flex-wrap gap-2 md:gap-3 text-[10px] md:text-[11px] tracking-[0.25em] md:tracking-[0.3em] uppercase opacity-90">
            <span className="text-xl" aria-hidden>{financial.flag}</span>
            <MapPin className="w-3 h-3" />
            <span className="truncate">{p.location}</span>
            {comingSoon && (
              <span className="ml-1 inline-flex items-center gap-1 bg-[var(--gp-gold)] text-[hsl(var(--primary))] text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full">
                <Hourglass className="w-3 h-3" /> {t('resortDetail.hero.badges.comingSoon', 'COMING SOON')}
              </span>
            )}
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] mt-3" data-testid="resort-title">{p.short_name || p.name}</h1>
          <p className="mt-3 md:mt-4 text-sm md:text-lg opacity-90 max-w-2xl">{p.tagline || p.description}</p>

          <div className="mt-5 md:mt-6 flex flex-wrap gap-2 md:gap-3">
            <Button size="lg" onClick={() => openBookSenior(`resort_${slug}_hero`)} data-testid="hero-cta-download" className="text-sm md:text-base">
              <Sparkles className="w-4 h-4 mr-2" /> {t('resortDetail.hero.ctaTalk', 'Parla con un Senior Associate')}
            </Button>
            {!comingSoon && (
              <Button size="lg" variant="secondary" onClick={() => openBookSenior(`resort_${slug}_hero_secondary`)} data-testid="hero-cta-call" className="text-sm md:text-base">
                <Calendar className="w-4 h-4 mr-2" /> {t('resortDetail.hero.ctaBookCall', 'Prenota una call')}
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="px-4 md:px-12 -mt-12 relative z-10">
        <div className="max-w-5xl mx-auto bg-[var(--surface-1)] border border-border rounded-2xl shadow-xl p-4 md:p-5 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 text-center">
          <StatCell v={financial.totalUnits} l={t('resortDetail.stats.totalUnits', 'Unità totali')} s={t('resortDetail.stats.totalUnitsSub', 'nel resort')} />
          {financial.equity?.available ? (
            <StatCell
              v={fmtEUR(financial.equity.quotaPrice)}
              l={t('resortDetail.stats.equityQuota', 'Quota Equity')}
              s={t('resortDetail.stats.equityQuotaSub', '{{available}}/{{total}} disponibili', { available: financial.equity.availableQuotas, total: financial.equity.totalQuotas })}
            />
          ) : (
            <StatCell
              v={financial.realEstate?.roi?.annual ? `${financial.realEstate.roi.annual}%` : '—'}
              l={t('resortDetail.stats.roiAnnual', 'ROI netto annuo')}
              s={financial.realEstate?.roi?.annual
                ? (financial.realEstate.roi.guaranteed === false
                    ? t('resortDetail.stats.roiIndicative', 'indicativo, non garantito')
                    : t('resortDetail.stats.roiFromDelivery', 'dalla consegna'))
                : t('resortDetail.stats.roiFromDelivery', 'dalla consegna')}
            />
          )}
          {reAvailable ? (
            <StatCell
              v={fmtEUR(financial.realEstate.startingFrom)}
              l={t('resortDetail.stats.startingFrom', 'A partire da')}
              s={t('resortDetail.stats.unitsAvailable', '{{available}}/{{total}} disponibili', { available: financial.units.available, total: financial.units.total })}
            />
          ) : financial.realEstate?.soldOut ? (
            <StatCell
              v="SOLD OUT"
              l={t('resortDetail.stats.realEstateSoldOut', 'Real Estate')}
              s={t('resortDetail.stats.realEstateSoldOutSub', 'Nuove disponibilità in comunicazione')}
            />
          ) : (
            <StatCell
              v={t('resortDetail.stats.comingSoon', 'Coming Soon')}
              l={t('resortDetail.stats.comingSoonType', 'Tipologia')}
              s={t('resortDetail.stats.comingSoonSub', 'In pubblicazione')}
            />
          )}
          <StatCell
            v={financial.grandOpening}
            l={t('resortDetail.stats.grandOpening', 'Grand Opening')}
            s={t('resortDetail.stats.grandOpeningSub', 'Apertura ufficiale')}
          />
        </div>
      </section>

      {/* ★ 2026-08-11 (Fase 37) — Video hero YouTube (subito dopo stats strip)
          Ogni resort ha il suo video. Se non ha video specifico usa il default.
          ★ 2026-08-13 (Fase 50) — Su Lombok il video "20 minuti" viene mostrato
          alla FINE (prima delle FAQ), non qui. Vedi sezione dedicata più sotto. */}
      {slug !== 'lombok' && (() => {
        const video = RESORT_VIDEOS[slug] || DEFAULT_RESORT_HERO_VIDEO;
        if (!video?.youtubeId) return null;
        return (
          <section className="px-4 md:px-12 mt-10 md:mt-14" data-testid="resort-hero-video">
            <div className="max-w-5xl mx-auto">
              <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-muted-foreground text-center mb-2">{t('resortDetail.sections.videoIntroEyebrow', 'Video presentazione')}</div>
              <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-[hsl(var(--primary))] text-center mb-4 md:mb-6">
                {t('resortDetail.sections.videoIntroTitle', 'Guarda {{name}} in 20 minuti', { name: p.short_name || p.name })}
              </h2>
              <div className="rounded-2xl overflow-hidden border border-border shadow-2xl aspect-video bg-black">
                <iframe
                  src={`https://www.youtube.com/embed/${video.youtubeId}?rel=0&modestbranding=1`}
                  title={video.title || p.name}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </section>
        );
      })()}

      {/* ★ 2026-08-13 (Fase 50) — LOMBOK: 4 video hero secondari sotto la hero.
          Layout griglia 2×2, aspect video. Contenuti: 4 clip verticali Bumbang Bay/drone/team/verification. */}
      {slug === 'lombok' && (
        <section className="px-4 md:px-12 mt-10 md:mt-14 max-w-6xl mx-auto" data-testid="lombok-hero-videos">
          <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-muted-foreground text-center mb-2">{t('resortDetail.sections.videoDeepDiveEyebrow', 'Video di approfondimento')}</div>
          <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-[hsl(var(--primary))] text-center mb-6">
            {t('resortDetail.sections.lombokClipsTitle', 'Green Paradise Lombok · in 4 clip')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {LOMBOK_HERO_VIDEOS.map((v, i) => (
              <div
                key={v.youtubeId}
                className="rounded-2xl overflow-hidden border border-border shadow-lg aspect-video bg-black"
                data-testid={`lombok-hero-video-${i}`}
              >
                <iframe
                  src={`https://www.youtube.com/embed/${v.youtubeId}?rel=0&modestbranding=1`}
                  title={v.title}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ★ 2026-08-13 (Fase 50) — LOMBOK: Tabella completa 220 appartamenti (100 disponibili · 120 venduti).
          Dati diretti dal Google Sheet ufficiale. Filtri: piano, stato, taglio.
          ★ 2026-08-14 (Fase 51) — DISABILITATA su richiesta utente: si ripete con la
          sezione "Scelta live" (path-section) sotto. Manteniamo il component in
          repo per possibile riabilitazione futura. */}
      {false && slug === 'lombok' && (
        <section className="px-4 md:px-12 py-12 md:py-16" data-testid="lombok-units-section">
          <div className="max-w-6xl mx-auto">
            <LombokUnitsTable
              projectSlug="lombok"
              onBookCall={(u) => openQualifica(`lombok_book_${u.code}`)}
            />
          </div>
        </section>
      )}

      {/* ★ 2026-08-13 (Fase 50) — LOMBOK: Sezione "Diventa socio" RIMOSSA su richiesta utente.
          Motivo: rischio profilo CONSOB (sollecitazione al pubblico risparmio non autorizzata).
          La logica backend (routes_lombok_socio.py) resta in place ma NON è più esposta pubblicamente.
          Se in futuro serve la si può riattivare solo dietro area riservata post-login/NDA. */}
      {false && slug === 'lombok' && (
        <section className="gp-section-accent px-4 md:px-12 py-14 md:py-20" data-testid="lombok-equity-socio-section">
          <LombokEquitySocio />
        </section>
      )}

      {/* ★ 2026-08-14 (Fase 51) — Nuova sezione unificata "Simulatore Live" su TUTTE le card.
          Sostituisce sia la vecchia "Scegli il tuo appartamento" (RoiCalculator) sia
          il LivePaymentPlan statico. Su La Mar / Narea la disponibilità unità è mantenuta. */}
      {(financial.realEstate?.available || financial.documents) && (
        <section className="px-4 md:px-12 py-12 md:py-16" data-testid="simulator-section">
          <div className="max-w-6xl mx-auto space-y-6">
            {/* Introduzione */}
            <div>
              <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-muted-foreground">{t('resortDetail.simulator.eyebrow', 'Simulatore Live')}</div>
              <h2 className="font-serif text-2xl md:text-3xl lg:text-5xl text-[hsl(var(--primary))] mt-1">
                {t('resortDetail.simulator.title', 'Costruisci il tuo piano rate')}
              </h2>
              <p className="text-xs md:text-sm text-muted-foreground mt-1.5 max-w-2xl">
                {financial.units?.available || financial.totalUnits ? (
                  <Trans
                    i18nKey="resortDetail.simulator.subtitleWithUnits"
                    values={{
                      available: financial.units?.available || financial.totalUnits,
                      blocks: financial.blocks?.total || '',
                      price: fmtEUR(financial.realEstate?.startingFrom),
                    }}
                    components={{ b: <b />, span: financial.blocks?.total ? <span /> : <span style={{ display: 'none' }} /> }}
                  />
                ) : (
                  <>{t('resortDetail.simulator.subtitleGeneric', "Piano pagamenti personalizzato secondo la tipologia e l'anticipo che preferisci.")}</>
                )}
                {comingSoon && (
                  <span className="inline-flex items-center gap-1 ml-2 px-2 py-0.5 rounded-full bg-[var(--gp-gold)]/15 border border-[var(--gp-gold)]/40 text-[10px] font-medium text-[hsl(var(--primary))]">
                    <Hourglass className="w-3 h-3" /> {t('resortDetail.simulator.comingSoonBadge', 'Coming Soon')}
                  </span>
                )}
              </p>
            </div>

            {/* Tabella disponibilità appartamenti — mostrata su TUTTI i progetti con
                dati unità nel DB (La Mar, Narea, Lombok). ★ 2026-08-14 (Fase 51.3)
                — Ripristinata anche su Lombok per non nascondere il catalogo. */}
            {financial.documents && (
              <PropertyUnitsTable slug={slug} />
            )}

            {/* Simulatore rate live — su TUTTE le card */}
            <PaymentSimulator
              slug={slug}
              grandOpening={financial.grandOpening}
              projectName={p?.name}
            />
          </div>
        </section>
      )}

      {/* Documenti di progetto (La Mar / Narea) */}
      {financial.documents && Object.keys(financial.documents || {}).length > 0 && (
        <section className="gp-section-accent px-4 md:px-12 py-12 md:py-16" data-testid="documents-section">
          <div className="max-w-6xl mx-auto">
            <ProjectDocuments documents={financial.documents} slug={slug} />
          </div>
        </section>
      )}

      {/* ★ 2026-07-29 — Promo banner (Chumphon pre-launch -30% fino a ottobre 2026) */}
      {financial.promo?.active && (
        <section className="px-4 md:px-12 mt-6" data-testid="promo-banner">
          <div className="max-w-5xl mx-auto rounded-2xl bg-gradient-to-r from-[var(--gp-gold)]/20 via-[var(--gp-gold)]/10 to-[var(--gp-gold)]/20 border-2 border-[var(--gp-gold)] p-4 md:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[var(--gp-gold)] text-[hsl(var(--primary))] grid place-items-center flex-shrink-0 shadow-md">
              <Sparkles className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-[var(--gp-gold)] font-bold">{t('resortDetail.promo.eyebrow', 'Promo pre-launch')}</div>
              <div className="font-serif text-lg md:text-xl text-[hsl(var(--primary))] mt-0.5">
                {t('resortDetail.promo.title', 'Tutti i prezzi scontati del -{{pct}}% fino a {{date}}', { pct: financial.promo.discountPct, date: financial.promo.endLabel })}
              </div>
              <div className="text-xs md:text-sm text-muted-foreground mt-1">
                {financial.promo.description}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ★ 2026-08-14 (Fase 51) — Vecchio blocco "Scegli il tuo appartamento" (RoiCalculator RE)
          DISABILITATO: sostituito interamente dal nuovo <PaymentSimulator /> sopra. Preserviamo
          il codice in `false && ...` per possibile riabilitazione futura. */}
      {false && financial.realEstate?.available && !['narea', 'la-mar-residence'].includes(slug) && (
        <section className="gp-section-accent" id="calc">
        <div className="px-4 md:px-12 py-12 md:py-16 max-w-6xl mx-auto" data-testid="path-section">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-5 md:mb-6">
            <div>
              <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-muted-foreground">Scegli il tuo appartamento</div>
              <h2 className="font-serif text-2xl md:text-3xl lg:text-5xl text-[hsl(var(--primary))] mt-1">
                Real Estate
              </h2>
              <p className="text-xs md:text-sm text-muted-foreground mt-1.5 md:mt-2 max-w-2xl">
                Compra un appartamento all&apos;interno del resort. Vedi subito la proiezione
                personalizzata sui dati del project financing ufficiale.
              </p>
            </div>
            {comingSoon && (
              <div className="px-3 py-1.5 rounded-full bg-[var(--gp-gold)]/15 border border-[var(--gp-gold)]/40 text-[10px] md:text-[11px] font-medium text-[hsl(var(--primary))] inline-flex items-center gap-1.5">
                <Hourglass className="w-3 h-3" /> Resort Coming Soon · In costruzione
              </div>
            )}
          </div>

          <div className="mt-8">
            <RoiCalculator
              slug={slug}
              mode="real_estate"
              onChangeMode={setMode}
              showTabs={false}
            />
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3 justify-center">
            <Button size="lg" onClick={() => openBookSenior(`resort_${slug}_path_real_estate`)} data-testid="path-cta-lead">
              <Sparkles className="w-4 h-4 mr-2" /> Parla con un Senior Associate
            </Button>
            <Button size="lg" variant="secondary" onClick={() => openBookSenior(`resort_${slug}_path_call`)} data-testid="path-cta-call">
              <Calendar className="w-4 h-4 mr-2" /> Prenota una call
            </Button>
          </div>
        </div>
      </section>
      )}

      {/* Resort video legacy — sotto il calcolatore. Ora RIDONDANTE per progetti con
          documenti perché il video hero è già sopra. Nascondo per La Mar Residence. */}
      {RESORT_VIDEOS[slug] && !financial.documents && (
        <section className="px-4 md:px-12 py-10 md:py-14 max-w-5xl mx-auto" data-testid={`resort-video-${slug}`}>
          <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-muted-foreground">{t('resortDetail.sections.verificationEyebrow', 'Verification Pass · testimonianza')}</div>
          <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-[hsl(var(--primary))] mt-1 leading-tight">
            {RESORT_VIDEOS[slug].title}
          </h2>
          <p className="text-xs md:text-sm text-muted-foreground mt-2 max-w-xl">{t('resortDetail.sections.verificationSubtitle', "Una testimonianza diretta di chi ha vissuto l'esperienza del Verification Pass HCF Group. Premi play per ascoltarla.")}</p>
          <div className="mt-5 md:mt-6 aspect-video rounded-2xl overflow-hidden border border-border bg-black shadow-lg">
            <iframe
              title={RESORT_VIDEOS[slug].title}
              src={`https://www.youtube.com/embed/${RESORT_VIDEOS[slug].youtubeId}?rel=0&modestbranding=1&autoplay=1&mute=0`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
              data-testid={`resort-video-iframe-${slug}`}
            />
          </div>
        </section>
      )}

      {/* Benefits Last 20 — solo per progetti Equity Share (nascosto per progetti solo Real Estate come La Mar Residence) */}
      {financial.equity?.available && (
      <section className="px-6 md:px-12 py-14 max-w-5xl mx-auto" data-testid="benefits-last20">
        <div className="flex items-center gap-2">
          <Star className="w-5 h-5 text-[var(--gp-gold)]" />
          <div className="text-xs tracking-[0.3em] uppercase text-[var(--gp-gold)]">{t('resortDetail.benefits.eyebrow', 'Benefit riservati')}</div>
        </div>
        <h2 className="font-serif text-3xl md:text-4xl text-[hsl(var(--primary))] mt-2">
          {t('resortDetail.benefits.title', 'Hai diritto ai benefit, ultimi {{n}} soci', { n: financial.equity.benefitsLastN })}
        </h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          <Trans
            i18nKey="resortDetail.benefits.subtitle"
            values={{ n: financial.equity.benefitsLastN, country: financial.country }}
            components={{ b: <b /> }}
          />
        </p>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {LAST20_BENEFITS.map((b) => (
            <div key={b.title} className="p-5 rounded-2xl border border-[var(--gp-gold)]/40 bg-gradient-to-br from-[var(--gp-gold)]/5 to-transparent">
              <div className="w-10 h-10 rounded-full bg-[var(--gp-gold)]/20 grid place-items-center">
                <Sparkles className="w-4 h-4 text-[var(--gp-gold)]" />
              </div>
              <div className="font-medium mt-3">{b.title}</div>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{b.desc}</p>
              <div className="text-[10px] uppercase tracking-wider text-[var(--gp-gold)] mt-3">{b.valueHint}</div>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* Rich apartment + resort showcase (data from BROCHURE_DATA per resort) */}
      {BROCHURE_DATA[slug] && (
        <section className="px-4 md:px-12 pb-16 max-w-6xl mx-auto" data-testid="resort-showcase">
          <ApartmentShowcase slug={slug} />
        </section>
      )}

      {/* Render gallery — grouped by area when `gallery_groups` is available, fallback to flat carousel */}
      {galleryGroups.length > 0 ? (
        <section className="px-4 md:px-12 pb-16 max-w-6xl mx-auto" data-testid="resort-gallery-grouped">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
            <div>
              <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-muted-foreground">{t('resortDetail.sections.galleryGroupsEyebrow', 'Render ufficiali')}</div>
              <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-[hsl(var(--primary))] mt-1 leading-tight">
                {t('resortDetail.sections.galleryGroupsTitle', 'Il progetto, area per area')}
              </h2>
              <p className="text-xs md:text-sm text-muted-foreground mt-2 max-w-2xl">
                {t('resortDetail.sections.galleryGroupsSubtitle', 'Render architettonici 1:1 estratti dal masterplan ufficiale. Ogni area è studiata per coerenza stilistica e funzionalità operativa del resort una volta completato.')}
              </p>
            </div>
          </div>

          <div className="space-y-10 md:space-y-14">
            {galleryGroups.map((group, gi) => (
              <div key={group.key || gi} data-testid={`gallery-group-${group.key || gi}`}>
                <div className="flex items-end justify-between flex-wrap gap-2 mb-4">
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--gp-gold)]">{t('resortDetail.sections.galleryGroupAreaLabel', '{{index}} · Area', { index: `0${gi + 1}`.slice(-2) })}</div>
                    <h3 className="font-serif text-xl md:text-2xl text-[hsl(var(--primary))] mt-1">{group.title}</h3>
                    {group.subtitle && (
                      <p className="text-xs md:text-sm text-muted-foreground mt-1 max-w-xl">{group.subtitle}</p>
                    )}
                  </div>
                  <div className="text-[10px] md:text-xs text-muted-foreground hidden md:block">
                    {t('resortDetail.sections.galleryGroupRenderCount', '{{count}} render', { count: (group.images || []).length })}
                  </div>
                </div>
                <Carousel
                  opts={{ align: 'start', loop: (group.images || []).length > 2 }}
                  className="w-full"
                  data-testid={`carousel-${group.key || gi}`}
                >
                  <CarouselContent className="-ml-3">
                    {(group.images || []).map((img, i) => (
                      <CarouselItem
                        key={img.src || `${group.key || gi}-${i}`}
                        className="pl-3 basis-full sm:basis-1/2 lg:basis-1/3"
                        data-testid={`gallery-${group.key || gi}-slide-${i}`}
                      >
                        <div className="aspect-[4/3] overflow-hidden rounded-2xl border border-border bg-[var(--surface-2)] relative group">
                          <img
                            src={img.src}
                            alt={img.caption || `${group.title} ${i + 1}`}
                            loading="lazy"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                          />
                          {img.caption && (
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 text-white text-[11px] md:text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                              {img.caption}
                            </div>
                          )}
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious
                    className="hidden md:flex -left-2 bg-white border-border shadow-lg"
                    data-testid={`gallery-${group.key || gi}-prev`}
                  />
                  <CarouselNext
                    className="hidden md:flex -right-2 bg-white border-border shadow-lg"
                    data-testid={`gallery-${group.key || gi}-next`}
                  />
                </Carousel>
              </div>
            ))}
          </div>
        </section>
      ) : (
        gallery.length > 0 && (
          <section className="px-6 md:px-12 pb-16 max-w-6xl mx-auto" data-testid="resort-gallery">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
              <div>
                <div className="text-xs tracking-[0.3em] uppercase text-muted-foreground">{t('resortDetail.sections.gallerySubEyebrow', 'Render aggiuntivi')}</div>
                <h2 className="font-serif text-3xl text-[hsl(var(--primary))] mt-1">{t('resortDetail.sections.gallerySubTitle', 'Altre prospettive')}</h2>
                <p className="text-sm text-muted-foreground mt-2 max-w-xl">{t('resortDetail.sections.gallerySubDescription', 'Render architettonici e viste esterne.')}</p>
              </div>
              <div className="text-xs text-muted-foreground hidden md:block">{t('resortDetail.sections.galleryImagesCount', '{{count}} immagini · scorrevoli', { count: gallery.length })}</div>
            </div>
            <Carousel
              opts={{ align: 'start', loop: true }}
              className="w-full"
              data-testid={`resort-carousel-${slug}`}
            >
              <CarouselContent className="-ml-3">
                {gallery.map((g, i) => (
                  <CarouselItem key={g || `slide-${i}`} className="pl-3 basis-full sm:basis-1/2 lg:basis-1/3" data-testid={`carousel-slide-${i}`}>
                    <div className="aspect-[4/3] overflow-hidden rounded-2xl border border-border bg-[var(--surface-2)]">
                      <img
                        src={g}
                        alt={`${p.name} — ${i + 1}`}
                        loading="lazy"
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="hidden md:flex -left-2 bg-white border-border shadow-lg" data-testid="carousel-prev" />
              <CarouselNext className="hidden md:flex -right-2 bg-white border-border shadow-lg" data-testid="carousel-next" />
            </Carousel>
          </section>
        )
      )}

      {/* Google Maps — location reale (presente quando il resort ha coordinate verificate) */}
      {googleMaps && (
        <section className="px-4 md:px-12 pb-16 max-w-6xl mx-auto" data-testid="resort-map-section">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
            <div>
              <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-muted-foreground">{t('resortDetail.sections.mapEyebrow', 'Dove si trova')}</div>
              <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-[hsl(var(--primary))] mt-1 leading-tight">
                {t('resortDetail.sections.mapTitle', 'Location reale & coordinate verificate')}
              </h2>
              <p className="text-xs md:text-sm text-muted-foreground mt-2 max-w-2xl">
                {t('resortDetail.sections.mapSubtitle', "Terreno già acquisito e censito. Apri Google Maps per vedere la posizione esatta del resort dall'alto e l'accesso alla spiaggia.")}
              </p>
              <div className="mt-3 inline-flex items-center gap-2 text-xs text-muted-foreground">
                <MapPin className="w-3.5 h-3.5 text-[var(--gp-gold)]" />
                <span>{googleMaps.address_short || p.location}</span>
                <span className="opacity-50">·</span>
                <span className="font-mono">{googleMaps.lat}, {googleMaps.lng}</span>
              </div>
            </div>
            <a
              href={googleMaps.share_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex"
              data-testid="map-open-google"
            >
              <Button size="sm" variant="secondary">
                <MapPin className="w-4 h-4 mr-2" /> {t('resortDetail.sections.mapCta', 'Apri in Google Maps')}
                <ExternalLink className="w-3 h-3 ml-2" />
              </Button>
            </a>
          </div>

          <div className="rounded-2xl overflow-hidden border border-border shadow-lg bg-[var(--surface-2)]">
            <div className="aspect-[16/9] md:aspect-[21/9] w-full">
              <iframe
                title={t('resortDetail.sections.mapAria', 'Mappa {{name}}', { name: p.name })}
                src={googleMaps.embed_url}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                data-testid="map-iframe"
              />
            </div>
          </div>
        </section>
      )}

      {/* ★ 2026-08-11 (Fase 37 update) — Video secondario (es. Verification Pass)
          Mostrato tra Documenti e FAQ per progetti che lo hanno configurato. */}
      {RESORT_SECONDARY_VIDEOS[slug] && (
        <section className="px-4 md:px-12 py-12 md:py-16" data-testid="resort-secondary-video">
          <div className="max-w-5xl mx-auto">
            <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-muted-foreground text-center mb-2">
              {RESORT_SECONDARY_VIDEOS[slug].label || t('resortDetail.sections.approfondimentoDefaultLabel', 'Approfondimento')}
            </div>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-[hsl(var(--primary))] text-center mb-4 md:mb-6">
              {RESORT_SECONDARY_VIDEOS[slug].title}
            </h2>
            <div className="rounded-2xl overflow-hidden border border-border shadow-2xl aspect-video bg-black">
              <iframe
                src={`https://www.youtube.com/embed/${RESORT_SECONDARY_VIDEOS[slug].youtubeId}?rel=0&modestbranding=1`}
                title={RESORT_SECONDARY_VIDEOS[slug].title}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>
      )}

      {/* ★ 2026-08-13 (Fase 50) — LOMBOK: Podcast/video "20 minuti" spostato alla FINE (prima FAQ).
          Su richiesta utente: sotto l'hero mettiamo i 4 clip corti, il podcast lungo qui in chiusura. */}
      {slug === 'lombok' && (() => {
        const video = RESORT_VIDEOS[slug] || DEFAULT_RESORT_HERO_VIDEO;
        if (!video?.youtubeId) return null;
        return (
          <section className="px-4 md:px-12 py-14 md:py-20 max-w-5xl mx-auto" data-testid="lombok-podcast-20min">
            <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-muted-foreground text-center mb-2">
              {t('resortDetail.sections.podcastEyebrow', 'Approfondimento completo')}
            </div>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-[hsl(var(--primary))] text-center mb-4 md:mb-6">
              {t('resortDetail.sections.videoIntroTitle', 'Guarda {{name}} in 20 minuti', { name: p.short_name || p.name })}
            </h2>
            <p className="text-xs md:text-sm text-muted-foreground text-center max-w-2xl mx-auto mb-6">
              {t('resortDetail.sections.podcastLombokSubtitle', "Il podcast completo di HCF Group per capire cosa stiamo costruendo a Lombok: masterplan, filiera costruttiva, timeline, e come funziona il piano pagamenti in 12 rate per l'acquisto dell'appartamento.")}
            </p>
            <div className="rounded-2xl overflow-hidden border border-border shadow-2xl aspect-video bg-black">
              <iframe
                src={`https://www.youtube.com/embed/${video.youtubeId}?rel=0&modestbranding=1`}
                title={video.title || p.name}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </section>
        );
      })()}

      {/* Resort-specific FAQ */}
      {RESORT_FAQS[slug] && (
        <>
          {/* ★ 2026-08-14 (Fase 52) — VisitsCounter sostituito da FakeViewersBadge:
              i contatori reali non si aggiornavano in real-time. Ora un badge
              "N persone stanno visitando" crea urgenza autentica in ogni card. */}
          <section className="px-4 md:px-12 pt-4 pb-2 max-w-4xl mx-auto w-full flex justify-center" data-testid="visits-social-proof">
            <FakeViewersBadge slug={slug} variant="default" />
          </section>
          <ResortFaq faqs={RESORT_FAQS[slug]} resortName={p.short_name || p.name} lang={i18n.language} />
        </>
      )}

      {/* CTA: Parla con un Senior Associate (unico CTA — Zoom diretta con Senior/CEO) */}
      <section className="gp-section-accent">
        <div className="px-6 md:px-12 py-16 max-w-5xl mx-auto text-center">
          <Sparkles className="w-8 h-8 mx-auto text-[hsl(var(--primary))]" />
          <h2 className="font-serif text-3xl md:text-4xl text-[hsl(var(--primary))] mt-3">{t('resortDetail.cta.title', 'Pronto a entrare nel progetto?')}</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl mx-auto">
            {t('resortDetail.cta.subtitle', 'Un nostro Senior Associate ti accompagna nella lettura dei documenti ufficiali — project financing, masterplan, governance — e ti spiega passo passo come si entra.')}
          </p>
          <Button
            size="lg"
            className="mt-5"
            onClick={() => openBookSenior(`resort_${slug}_final_cta`)}
            data-testid="resort-cta-qualifica"
          >
            <Sparkles className="w-4 h-4 mr-2" /> {t('resortDetail.cta.button', 'Parla con un Senior Associate')}
          </Button>
        </div>
      </section>

      {/* ★ 2026-08-13 (Fase 51 · Compliance CONSOB) — Legal disclaimer in fondo a ogni resort */}
      <LegalDisclaimerBox />

      {/* ★ 2026-08-14 (Fase 51) — Modal Prenota Zoom con Senior/CEO
          (usato da tutti i CTA della pagina resort per unificare il flusso) */}
      <BookSeniorModal
        open={bookOpen}
        onOpenChange={setBookOpen}
        slug={slug}
        context={bookContext}
      />

      {/* Lead-Gate Modal */}
      {gateOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => !submitted && setGateOpen(false)} data-testid="resort-gate-modal">
          <div className="max-w-md w-full bg-card rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4" />
                <div className="font-serif text-lg">{submitted ? t('resortDetail.gate.titleOpen', 'Documenti pronti') : t('resortDetail.gate.titleClosed', 'Sblocca i documenti')}</div>
              </div>
              <button onClick={() => setGateOpen(false)} className="text-muted-foreground hover:text-foreground" data-testid="resort-gate-close"><X className="w-4 h-4" /></button>
            </div>
            {!submitted ? (
              <div className="p-5 space-y-3">
                <div className="text-xs text-muted-foreground">{t('resortDetail.gate.description', 'Bastano 30 secondi. Riceverai brochure + project financing via email e potrai scaricarli subito qui.')}</div>
                <div className="space-y-1.5"><Label>{t('resortDetail.gate.name', 'Nome*')}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="resort-gate-name" /></div>
                <div className="space-y-1.5"><Label>{t('resortDetail.gate.email', 'Email*')}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="resort-gate-email" /></div>
                <div className="space-y-1.5"><Label>{t('resortDetail.gate.phone', 'Telefono / Telegram')}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t('resortDetail.gate.phonePlaceholder', '+39…')} data-testid="resort-gate-phone" /></div>
                <div className="space-y-1.5">
                  <Label>{t('resortDetail.gate.interest', 'Cosa ti interessa?')}</Label>
                  <div className="grid grid-cols-1 gap-2">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, interest_type: 'apartment' })}
                      className={`text-xs rounded-md px-3 py-2 border ${form.interest_type === 'apartment' ? 'border-[hsl(var(--primary))] bg-[hsl(var(--accent))]' : 'border-border'}`}
                      data-testid="resort-gate-interest-apartment"
                    >
                      {t('resortDetail.gate.interestApartment', 'Acquisto appartamento (Real Estate)')}
                    </button>
                  </div>
                </div>
                <label className="flex items-start gap-2 text-[11px] text-muted-foreground">
                  <input type="checkbox" className="mt-0.5" checked={form.consent_privacy} onChange={(e) => setForm({ ...form, consent_privacy: e.target.checked })} data-testid="resort-gate-consent" />
                  <span>{t('resortDetail.gate.consent', 'Acconsento al trattamento dei dati ai sensi del GDPR.')}</span>
                </label>
                <Button className="w-full" onClick={submit} disabled={submitting} data-testid="resort-gate-submit">
                  {submitting ? t('resortDetail.gate.submitting', 'Invio…') : t('resortDetail.gate.submit', 'Sblocca documenti')}
                </Button>
              </div>
            ) : (
              <div className="p-5 space-y-3" data-testid="resort-gate-success">
                <CheckCircle2 className="w-10 h-10 text-[hsl(var(--primary))]" />
                <div className="text-sm">
                  <Trans i18nKey="resortDetail.gate.successHeader" values={{ name: p.name }} components={{ b: <b /> }} />
                </div>
                <a href={submitted.downloads.brochure_url} target="_blank" rel="noreferrer" className="block w-full" data-testid="resort-download-brochure">
                  <Button variant="secondary" className="w-full justify-start"><FileText className="w-4 h-4 mr-2" /> {t('resortDetail.gate.downloadBrochure', 'Brochure ufficiale')} <ExternalLink className="w-3 h-3 ml-auto" /></Button>
                </a>
                <a href={submitted.downloads.financing_url} target="_blank" rel="noreferrer" className="block w-full" data-testid="resort-download-financing">
                  <Button variant="secondary" className="w-full justify-start"><FileText className="w-4 h-4 mr-2" /> {t('resortDetail.gate.downloadFinancing', 'Project Financing')} <ExternalLink className="w-3 h-3 ml-auto" /></Button>
                </a>
                <div className="text-[11px] text-muted-foreground">{t('resortDetail.gate.sentByEmail', "Te li abbiamo inviati anche all'email che hai indicato.")}</div>
                <Button className="w-full" onClick={() => nav(submitted.next_step_url)} data-testid="resort-go-vsl">
                  {t('resortDetail.gate.continueVsl', 'Continua con la VSL')} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCell({ v, l, s }) {
  return (
    <div>
      <div className="font-serif text-2xl md:text-3xl text-[hsl(var(--primary))]">{v}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{l}</div>
      <div className="text-[10px] text-muted-foreground mt-0.5">{s}</div>
    </div>
  );
}

function PathCards({ mode, setMode, financial }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="path-cards">
      <button
        type="button"
        onClick={() => setMode('equity')}
        className={`text-left p-5 rounded-2xl border transition-all ${mode === 'equity' ? 'border-[hsl(var(--primary))] bg-[hsl(var(--accent))] shadow-lg' : 'border-border bg-[var(--surface-1)] hover:border-[hsl(var(--primary))]/40'}`}
        data-testid="path-card-equity"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-[var(--gp-gold)]/20 grid place-items-center">
            <Sparkles className="w-5 h-5 text-[var(--gp-gold)]" />
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--gp-gold)]">Percorso A</div>
        </div>
        <div className="font-serif text-2xl mt-2 text-[hsl(var(--primary))]">Equity Share</div>
        <p className="text-sm text-muted-foreground mt-2">
          Diventi socio del resort. Partecipi al rendimento operativo del business turistico, distribuzioni periodiche, exit pianificato.
        </p>
        <div className="mt-3 text-xs flex flex-wrap gap-2">
          <span className="px-2 py-1 rounded-full bg-[var(--gp-gold)]/15 text-[hsl(var(--primary))] font-medium">
            {financial.equity.availableQuotas} quote su {financial.equity.totalQuotas} · {fmtEUR(financial.equity.quotaPrice)}
          </span>
          {financial.promo?.active && (
            <span className="px-2 py-1 rounded-full bg-[var(--gp-gold)] text-[hsl(var(--primary))] font-bold shadow-sm" data-testid="promo-badge">
              -{financial.promo.discountPct}% pre-launch · fino a {financial.promo.endLabel}
            </span>
          )}
          {/* CONSOB CLEANUP: rimosso pill "Target Yield" — niente promesse di
              rendimento sulla landing pubblica. Gli scenari (Prudente/Medio/
              Aggressivo) restano disponibili solo dentro il RoiCalculator,
              con disclaimer espliciti. */}
        </div>
      </button>

      <button
        type="button"
        onClick={() => setMode('real_estate')}
        className={`text-left p-5 rounded-2xl border transition-all ${mode === 'real_estate' ? 'border-[hsl(var(--primary))] bg-[hsl(var(--accent))] shadow-lg' : 'border-border bg-[var(--surface-1)] hover:border-[hsl(var(--primary))]/40'}`}
        data-testid="path-card-real-estate"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-[var(--gp-teal)]/20 grid place-items-center">
            <Home className="w-5 h-5 text-[var(--gp-teal)]" />
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--gp-teal)]">Percorso B</div>
        </div>
        <div className="font-serif text-2xl mt-2 text-[hsl(var(--primary))]">Real Estate</div>
        <p className="text-sm text-muted-foreground mt-2">
          Acquisti un appartamento dentro il resort. Property management completo, accesso ai servizi, possibilità di rendita locativa.
        </p>
        <div className="mt-3 text-xs flex flex-wrap gap-2">
          <span className="px-2 py-1 rounded-full bg-[var(--gp-teal)]/15 text-[hsl(var(--primary))] font-medium">
            Da {fmtEUR(financial.realEstate.startingFrom)}
          </span>
          <span className="px-2 py-1 rounded-full bg-[var(--surface-2)] text-muted-foreground">
            Anticipo 10% · Grand opening Dic 2028
          </span>
        </div>
      </button>
    </div>
  );
}

function renderFaqAnswer(text) {
  // ★ 2026-08-11 (Fase 37 update) — Supporta newline + URL cliccabili nelle FAQ
  // ★ 2026-08-26 (Fase 55) — Hardening: accetta anche oggetti multi-lingua {it,en,es}
  // via fallback IT (o prima chiave disponibile). Il chiamante dovrebbe passare
  // sempre una stringa (via pickText), ma difendiamo da regressioni.
  if (text == null) return null;
  let safe = text;
  if (typeof safe !== 'string') {
    if (typeof safe === 'object') {
      safe = safe.it || safe.en || Object.values(safe)[0] || '';
    } else {
      safe = String(safe);
    }
  }
  if (!safe) return null;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const lines = safe.split('\n');
  return lines.map((line, li) => {
    const parts = line.split(urlRegex);
    return (
      <React.Fragment key={`fl-${li}`}>
        {li > 0 && <br />}
        {parts.map((part, pi) => {
          if (urlRegex.test(part)) {
            // reset regex state (regex.test is stateful when /g)
            urlRegex.lastIndex = 0;
            return (
              <a
                key={`lnk-${li}-${pi}`}
                href={part}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[hsl(var(--primary))] underline hover:no-underline break-all"
              >
                {part}
              </a>
            );
          }
          return <React.Fragment key={`t-${li}-${pi}`}>{part}</React.Fragment>;
        })}
      </React.Fragment>
    );
  });
}

function ResortFaq({ faqs, resortName, lang = 'it' }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(0);
  return (
    <section className="px-4 md:px-12 py-12 md:py-16 max-w-4xl mx-auto" data-testid="resort-faq">
      <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-muted-foreground">{t('resortDetail.faq.eyebrow', 'Domande frequenti')}</div>
      <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-[hsl(var(--primary))] mt-1 leading-tight">
        {t('resortDetail.faq.title', 'Risposte specifiche su {{name}}.', { name: resortName })}
      </h2>
      <p className="text-xs md:text-sm text-muted-foreground mt-2 max-w-2xl">
        {t('resortDetail.faq.subtitle', "10 domande che riceviamo più spesso su questo resort. Se la tua non c'è, scrivi all'AI in basso a destra o richiedi una call.")}
      </p>
      <div className="mt-6 divide-y divide-border border border-border rounded-2xl bg-[var(--surface-1)] overflow-hidden">
        {faqs.map((f, i) => {
          const isOpen = open === i;
          const q = pickText(f.q, lang);
          const a = pickText(f.a, lang);
          return (
            <button
              key={`faq-${i}`}
              type="button"
              onClick={() => setOpen(isOpen ? -1 : i)}
              aria-expanded={isOpen}
              className="w-full text-left p-4 md:p-5 hover:bg-[var(--surface-2)] transition-colors"
              data-testid={`resort-faq-${i}`}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="font-serif text-sm md:text-base text-[hsl(var(--primary))] pr-2">{q}</span>
                <ChevronLeft className={`w-4 h-4 shrink-0 text-muted-foreground transition-transform ${isOpen ? '-rotate-90' : 'rotate-180'}`} />
              </div>
              {isOpen && (
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed" data-testid={`resort-faq-${i}-answer`}>{renderFaqAnswer(a)}</p>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}
